import { useState } from 'react';
import { Outlet, NavLink, Link, useLocation } from 'react-router-dom';
import { UserButton } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { userApi } from '../services/api';

const navItems = [
  { to: '/stores/find', label: 'Find Stores', icon: '🔍' },
  { to: '/stores', label: 'My Stores', icon: '🏪' },
  { to: '/revenue', label: 'Revenue', icon: '💰' },
  { to: '/schedule', label: 'Schedule', icon: '📅' },
  { to: '/markets', label: 'Markets & Deals', icon: '🗓️' },
  { to: '/settings', label: 'Settings', icon: '⚙️' },
];

// Pages that should be full-bleed (no padding/max-width)
const fullBleedPages = ['/stores/find', '/stores'];

export function Layout() {
  const location = useLocation();
  const isFullBleed = fullBleedPages.includes(location.pathname);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: currentUser } = useQuery({
    queryKey: ['user'],
    queryFn: () => userApi.get(),
  });

  return (
    <div className="h-screen bg-gray-50 flex flex-col overflow-hidden">
      {/* Header */}
      <header className="bg-white shadow-sm z-20 shrink-0">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex h-14 sm:h-16 items-center justify-between">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2 -ml-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md"
              aria-label="Toggle menu"
            >
              {mobileMenuOpen ? (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ) : (
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              )}
            </button>

            <Link to="/" className="text-lg sm:text-xl font-semibold text-gray-900 truncate hover:text-blue-600 transition-colors">
              Territory Scheduler
            </Link>

            <div className="flex items-center gap-4 sm:gap-6">
              {/* Desktop navigation */}
              <nav className="hidden md:flex space-x-1">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-blue-100 text-blue-700'
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                ))}
                {currentUser?.is_admin && (
                  <NavLink
                    to="/admin"
                    className={({ isActive }) =>
                      `px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-purple-100 text-purple-700'
                          : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                      }`
                    }
                  >
                    Admin
                  </NavLink>
                )}
              </nav>
              <UserButton
                afterSignOutUrl="/sign-in"
                appearance={{
                  elements: {
                    avatarBox: 'w-8 h-8 sm:w-9 sm:h-9',
                  },
                }}
              />
            </div>
          </div>
        </div>

        {/* Mobile navigation dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 bg-white">
            <nav className="px-2 py-2 space-y-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-base font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-blue-100 text-blue-700'
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                    }`
                  }
                >
                  <span>{item.icon}</span>
                  {item.label}
                </NavLink>
              ))}
              {currentUser?.is_admin && (
                <NavLink
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 px-3 py-3 text-base font-medium rounded-md transition-colors ${
                      isActive
                        ? 'bg-purple-100 text-purple-700'
                        : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                    }`
                  }
                >
                  <span>🔐</span>
                  Admin
                </NavLink>
              )}
            </nav>
          </div>
        )}
      </header>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/20 z-10"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Main content */}
      <main className={isFullBleed ? 'flex-1 min-h-0' : 'flex-1 min-h-0 overflow-y-auto mx-auto max-w-7xl w-full px-4 py-4 sm:py-6 sm:px-6 lg:px-8'}>
        <Outlet />
      </main>
    </div>
  );
}
